import { useDisclosure } from '@chakra-ui/react';
import { IPaymentMethodResp } from '@monetize/types/app';
import {
  ApiListResponse,
  DEFAULT_API_RESPONSE,
  DEFAULT_PAGER,
  FilterType,
  GetListApiFilter,
  TDataTablePager,
} from '@monetize/types/common';
import { UseQueryOptions } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useGetPaymentMethodsByAccount } from '../../api/accountsService';
import { buildFilterParamsRequestObject } from '../../utils';

export const usePaymentMethodsData = (
  accountId: string,
  queryOptions?: Partial<
    UseQueryOptions<ApiListResponse<IPaymentMethodResp>, unknown, any>
  >,
) => {
  const [filters, setFilters] = useState<FilterType[]>([]);
  const [searchKey] = useState<string>('paymentMethodName');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pager, setPager] = useState<TDataTablePager>(DEFAULT_PAGER);
  const {
    open: isModalOpen,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const [filterParams, setFilterParams] = useState<GetListApiFilter>(() =>
    buildFilterParamsRequestObject(filters, searchTerm, searchKey),
  );

  const {
    isLoading,
    data: paymentMethods,
    isFetching,
  } = useGetPaymentMethodsByAccount(
    {
      accountId,
      config: pager,
      filters: filterParams,
    },
    { meta: { showErrorToast: true }, ...queryOptions },
  );

  useEffect(() => {
    setFilterParams(
      buildFilterParamsRequestObject(filters!, searchTerm, searchKey),
    );
  }, [filters, searchTerm, pager, searchKey]);

  const [selected, setSelected] = useState<null | IPaymentMethodResp>(null);

  const openCreateModal = () => {
    onOpenModal();
  };

  const openEditModal = (row: IPaymentMethodResp) => {
    setSelected(row);
    onOpenModal();
  };

  const onResetFilter = () => {
    setFilters([]);
  };

  return {
    pager,
    setPager,
    filters,
    setFilters,
    searchTerm,
    setSearchTerm,
    onResetFilter,
    totalRecords: paymentMethods?.totalElements || 0,
    loading: isLoading,

    isModalOpen,
    onOpenModal,
    onCloseModal: () => {
      setSelected(null);
      onCloseModal();
    },
    openCreateModal,
    openEditModal,
    // data should never be undefined, but return default response as a fallback
    listData: paymentMethods || DEFAULT_API_RESPONSE,
    // @deprecated - children do not need to worry about when data should be loaded
    // loadData,
    selected,
    setSelected,
    isFetching,
  };
};
